export const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const formatRelativeTime = (date: Date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return formatDate(date);
};

export const extractVariables = (text: string): string[] => {
  const matches = text.match(/\[([^\]]+)\]/g);
  if (!matches) return [];
  return [...new Set(matches.map(match => match.slice(1, -1)))].sort();
};

export const extractVariablesWithCounts = (text: string): { name: string; count: number }[] => {
  const matches = text.match(/\[([^\]]+)\]/g);
  if (!matches) return [];

  const variableCounts: Record<string, number> = {};
  matches.forEach(match => {
    const variable = match.slice(1, -1);
    variableCounts[variable] = (variableCounts[variable] || 0) + 1;
  });

  return Object.entries(variableCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const highlightText = (text: string, query: string): string => {
  if (!query) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy text:', err);
    return false;
  }
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    // Don't execute debounced functions when tab is not visible
    if (document.hidden) {
      return;
    }
    
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const getAuthErrorMessage = (error: any): string => {
  const message = error?.message || '';
  
  // Log the exact error for debugging
  console.error('Auth error details:', { message, error });
  
  // Check for SSL-related errors first
  if (message.includes('SSL') || message.includes('certificate') || message.includes('net::ERR_SSL')) {
    return 'Secure connection issue detected. Please refresh the page and try again. If the issue persists, try clearing your browser cache.';
  }
  
  if (message.includes('Invalid login credentials')) {
    return 'Invalid email or password. If you just signed up, please check your email and click the confirmation link first.';
  }
  if (message.includes('Email not confirmed')) {
    return 'Please check your email and click the confirmation link before signing in.';
  }
  if (message.includes('Email not confirmed') || message.includes('signup_disabled')) {
    return 'Please check your email and click the confirmation link to activate your account before signing in.';
  }
  if (message.includes('User not found')) {
    return 'No account found with this email address. Please sign up first.';
  }
  if (message.includes('Too many requests')) {
    return 'Too many login attempts. Please wait a few minutes before trying again.';
  }
  if (message.includes('Password should be at least')) {
    return 'Password must be at least 6 characters long.';
  }
  if (message.includes('Unable to validate email address')) {
    return 'Please enter a valid email address.';
  }
  if (message.includes('User already registered')) {
    return 'An account with this email already exists. Please sign in instead.';
  }
  if (message.includes('Invalid user')) {
    return 'Account not found or not activated. Please check your email for a confirmation link.';
  }
  if (message.includes('Invalid credentials')) {
    return 'Login failed. If you recently signed up, please confirm your email first. Otherwise, check your password.';
  }
  
  // Return the original message for debugging, but make it user-friendly
  return `Login failed: ${message || 'Please check your credentials and try again. If you just signed up, confirm your email first.'}`;
};